import { Controller } from 'stimulus'
import trackBusy from '../lib/metrics/busy'
import renderEmptyNote, { rangePhrase } from '../lib/metrics/empty_note'
import { createSearchList, renderFilterChips } from '../lib/metrics/search_list'
import JobChipFilters from '../lib/metrics/job_chips'
import {
  stackedColumns, hBars, lineChart, kpiTile, renderTable, seriesColor, fmtNum, fmtDate,
  groupedColumns, multiLine, hBarsStacked, SLOTS
} from '../lib/metrics/charts'

const GROUP_NAMES = ['Group A', 'Group B', 'Group C']

// "Jobs in these metrics" card: page size, sortable columns (param value
// -> label) and each key's first direction when picked.
const JOB_LIST_PAGE = 25
const JOB_LIST_SORT_DESC_FIRST = ['id', 'created', 'completed', 'lead']
// Cards reload this long after the last include/exclude click, so a run of
// clicks costs one reload.
const EXCLUDE_RELOAD_DELAY = 300

function fmtDay(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Search-list filters that take several picks (match any): the param each
// one sends as key[], the Stimulus target its list renders into, and the
// list's wording. Picks only (no "All containing" terms): the lists are
// short, so search just narrows them.
const PICK_FILTERS = [
  {
    key: 'assignee', target: 'assigneeSearch', label: 'Assignee', role: 'assignee', nounOne: 'person', nounMany: 'people'
  },
  {
    key: 'requester', target: 'requesterSearch', label: 'Requester', role: 'requester', nounOne: 'person', nounMany: 'people'
  },
  {
    key: 'parent_template_id', target: 'templateSearch', label: 'Template', nounOne: 'template', nounMany: 'templates'
  }
]

// A filter's values from the URL: key[]=a&key[]=b (multi-select, new
// saved views) or key=a (older saved views and shared links).
function urlValues(url, key) {
  return url.getAll(`${key}[]`).concat(url.getAll(key)).filter(v => v !== '')
}

// URLSearchParams from a params object; array values go as key[]=v.
function toSearchParams(params) {
  const p = new URLSearchParams()
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (Array.isArray(value)) value.forEach(v => p.append(`${key}[]`, v))
    else if (value !== undefined && value !== null) p.append(key, value)
  })
  return p
}

// Drives the Metrics screen: collects the filter values, fetches the
// per-card JSON endpoints in parallel, and renders each card as its
// response arrives (no single blocking request).
export default class extends Controller {
  static targets = [
    'range', 'assigneeSearch', 'requesterSearch', 'templateSearch', 'pickLabels', 'groupBy', 'emptyNote',
    'filterPicker', 'chipSearch', 'chips',
    'kpiCreated', 'kpiCompleted', 'kpiLead', 'kpiOnTime',
    'weeklyChart', 'weeklyLegend', 'weeklyTable', 'weeklySub',
    'statusChart', 'statusTable',
    'leadChart', 'leadTable',
    'spanFrom', 'spanTo', 'spanKpis', 'spanLineTitle', 'spanLineSub', 'spanChart',
    'spanHist', 'spanHistSub', 'spanTable',
    'jobsPanel', 'resultsPanel', 'trendsPanel', 'technicianPanel', 'moreMenu', 'moreCount',
    'viewMenu', 'menuDefaultLabel', 'menuPinLabel',
    'compareBtn', 'groupRows', 'compareGrid', 'singleGrid',
    'cmpTiles', 'cmpLegend', 'cmpWeekly', 'cmpLead', 'cmpMix', 'cmpMixLegend', 'cmpStatusTable',
    'jobList', 'jobListCount', 'jobListIncludeAll', 'jobListMore'
  ];

  connect() {
    this.groups = []
    // exclude[]: job ids (strings) left out of every card, per view
    this.excluded = []
    this.jobListSort = { sort: 'created', dir: 'desc' }
    this.buildPickLists()
    this.jobChips = new JobChipFilters({
      picker: this.filterPickerTarget,
      anchor: this.chipSearchTarget,
      urls: {
        tag: this.data.get('tagValuesUrl'),
        column: this.data.get('columnValuesUrl'),
        field: this.data.get('fieldValuesUrl')
      },
      baseParams: () => toSearchParams(this.filterParams()),
      onChange: () => {
        this.renderChips()
        this.load()
      },
      onLabels: () => this.renderChips()
    })
    this.initFromUrl()
    this.jobChips.ensureLabels()
    const url = new URLSearchParams(window.location.search)
    if (['results', 'trends', 'technician'].includes(url.get('tab'))) this.activateTab(url.get('tab'))
    else this.filterViewPills('jobs')
    this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      // only the visible tab re-renders — a hidden panel measures 0 wide and
      // the charts would fall back to a fixed 640px
      this.resizeTimer = setTimeout(() => { if (this.activeTab() === 'jobs') this.loadCards() }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler)
    clearTimeout(this.excludeTimer)
    // Turbolinks caches the page: drop the popovers and their document
    // listeners (connect() rebuilds the controls from the anchors)
    Object.values(this.pickLists || {}).forEach(list => list.destroy())
    if (this.jobChips) this.jobChips.destroy()
  }

  // Assignee / Requester / Template search lists. Counts are jobs created
  // or completed in the range with that person or template, under every
  // other filter on the tab (not the field's own picks). Labels for the
  // URL's picks come with the page (data-labels), so the buttons read
  // names before any list is opened.
  buildPickLists() {
    let known = {}
    try { known = JSON.parse(this.pickLabelsTarget.dataset.labels || '{}') } catch (e) { known = {} }
    const knownKey = { assignee: 'assignee', requester: 'requester', parent_template_id: 'template' }
    this.pickLists = {}
    PICK_FILTERS.forEach((f) => {
      const labels = new Map(Object.entries(known[knownKey[f.key]] || {}))
      this.pickLists[f.key] = createSearchList(this[`${f.target}Target`], {
        label: f.label,
        placeholder: 'All',
        searchPlaceholder: `Search ${f.nounMany}`,
        nounOne: f.nounOne,
        nounMany: f.nounMany,
        countNoun: 'jobs',
        supportsTerms: false,
        outOfRangeText: 'no jobs in range',
        labels,
        fetchOptions: (q, state) => this.fetchPickOptions(f, q, state),
        onApply: () => {
          this.renderChips()
          this.load()
        }
      })
    })
  }

  pickValues(key) {
    return this.pickLists[key].getState().picks
  }

  // One pick list's options: every other filter applies, its own picks
  // ride along as selected[] (they always come back, "(no jobs in range)"
  // when they have none).
  fetchPickOptions(filter, q, state) {
    const params = this.filterParams()
    delete params[filter.key]
    const p = toSearchParams({ ...params, list: '1' })
    if (filter.role) p.set('role', filter.role)
    if (q) p.set('q', q)
    state.picks.forEach(v => p.append('selected[]', v))
    this.jobChips.appendParams(p)
    const url = filter.role ? this.data.get('memberOptionsUrl') : this.data.get('templateOptionsUrl')
    return fetch(`${url}?${p}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
  }

  // Honor filters arriving via URL (saved-view links, shared URLs)
  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if ([...url.keys()].length === 0) return
    this.setSelect(this.rangeTarget, url.get('range'), '90')
    // Every pick is kept, listed or not: dropping one would silently turn
    // "Alex's jobs" into "all jobs". key[]= (new views) or key= (older
    // saved views and shared links).
    PICK_FILTERS.forEach((f) => {
      const picks = urlValues(url, f.key)
      if (picks.length) this.pickLists[f.key].setState({ terms: [], picks, excludes: [] })
    })
    this.setSelect(this.groupByTarget, url.get('group_by'), 'status')
    this.setSelect(this.spanFromTarget, url.get('span_from'), this.spanFromTarget.value)
    this.setSelect(this.spanToTarget, url.get('span_to'), this.spanToTarget.value)
    this.excluded = [...new Set(urlValues(url, 'exclude').filter(v => /^\d+$/.test(v)))]
    // tags[] / columns[] / fields[]: exact values or ~terms
    this.jobChips.initFromUrl(url)
    this.renderChips()
    const rawGroups = url.getAll('groups[]')
    if (rawGroups.length >= 2 && url.get('tab') !== 'results') {
      const options = JSON.parse(this.groupRowsTarget.dataset.columnOptions || '[]')
      this.groups = rawGroups.slice(0, 3).map((raw, slot) => ({
        slot,
        filters: raw.split('|').filter(Boolean).map((pair) => {
          const [columnId, value] = pair.split(/:(.*)/s)
          const col = options.find(o => String(o.id) === columnId)
          return { columnId, columnName: col ? col.name : `Column #${columnId}`, value }
        })
      }))
      this.enterCompare()
    }
  }

  // Every card, plus the jobs list when its filters changed (the list keeps
  // its loaded pages and sort across resizes, tab switches, Group by and
  // include/exclude clicks).
  load() {
    // "rush · 12 values" term counts are faceted like the lists: recount
    // them whenever the filters change
    const filterKey = this.queryString()
    if (filterKey !== this.lastFilterKey) {
      this.lastFilterKey = filterKey
      this.jobChips.refreshTermCounts()
    }
    this.loadCards()
    if (!this.compareActive()) this.loadJobList()
  }

  loadCards() {
    this.loadSeq = (this.loadSeq || 0) + 1
    if (this.compareActive()) {
      this.loadCompare()
      return
    }
    const qs = this.queryString()
    this.fetchJson(`${this.data.get('kpisUrl')}?${qs}`, data => this.renderKpis(data))
    this.fetchJson(`${this.data.get('weeklyUrl')}?${qs}`, data => this.renderWeekly(data))
    this.fetchJson(`${this.data.get('statusTimeUrl')}?${qs}`, data => this.renderStatusTime(data))
    this.fetchJson(`${this.data.get('leadTrendUrl')}?${qs}`, data => this.renderLeadTrend(data))
    this.loadSpan()
  }

  rangeChanged() {
    this.load()
  }

  // "Nothing in this range" note: zero jobs created AND completed in the
  // range. Names the newest matching job outside it (kpis sends
  // latest_created_at only then) and offers Last 12 months.
  renderRangeNote(data) {
    const cur = data.current
    if (this.compareActive() || cur.created || cur.completed) {
      renderEmptyNote(this.emptyNoteTarget, null)
      return
    }
    const range = this.rangeTarget.value
    renderEmptyNote(this.emptyNoteTarget, {
      noun: 'jobs',
      rangeLabel: rangePhrase(range),
      latest: data.latest_created_at || null,
      onWiden: range === '365' ? null : () => this.widenRange()
    })
  }

  widenRange() {
    this.setSelect(this.rangeTarget, '365', '365')
    this.rangeChanged()
  }

  busyEl() { return this.jobsPanelTarget }

  // Guarded responses belonging to a superseded load() are dropped, so a
  // slow older request can't paint over a newer one.
  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    const req = fetch(url, { headers: { Accept: 'application/json' } })
    if (guarded) trackBusy(this.busyEl(), req)
    req
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (guarded && seq !== (this.loadSeq || 0)) return
        onData(data)
      })
      .catch(() => {})
  }

  // Multi-value filters are arrays (sent as key[]=); empty ones are left out.
  filterParams() {
    const params = { range: this.rangeTarget.value }
    PICK_FILTERS.forEach(({ key }) => {
      const values = this.pickValues(key)
      if (values.length) params[key] = values
    })
    if (this.excluded.length) params.exclude = this.excluded
    return params
  }

  queryString() {
    const p = toSearchParams({ ...this.filterParams(), group_by: this.groupByTarget.value })
    this.appendChipParams(p)
    return p.toString()
  }

  // ---- chips: the pick lists, the tag / job column / job field filters
  // (lib/metrics/job_chips; sources AND-stacked on top of the other
  // filters, one source's values any-of server-side) and the excluded
  // jobs. A chip body reopens its list; ✕ removes it. ----

  filterChosen() {
    this.jobChips.sourceChosen()
  }

  renderChips() {
    const host = this.chipsTarget
    host.innerHTML = ''
    host.classList.add('m-sl-chips')
    PICK_FILTERS.forEach(({ key }) => renderFilterChips(host, this.pickLists[key].chips()))
    renderFilterChips(host, this.jobChips.chips())
    if (this.excluded.length) {
      const chip = document.createElement('span')
      chip.className = 'm-chip m-chip-excl'
      chip.title = 'Left out of every card on this tab. Uncheck or check jobs in the list at the bottom.'
      const label = document.createElement('span')
      const strong = document.createElement('strong')
      strong.textContent = `${fmtNum(this.excluded.length)} ${this.excluded.length === 1 ? 'job' : 'jobs'}`
      label.appendChild(strong)
      label.appendChild(document.createTextNode(' excluded'))
      chip.appendChild(label)
      const x = document.createElement('button')
      x.type = 'button'
      x.textContent = '✕'
      x.title = 'Include all jobs again'
      x.dataset.action = 'click->metrics#includeAllJobs'
      chip.appendChild(x)
      host.appendChild(chip)
    }
  }

  appendChipParams(p) {
    this.jobChips.appendParams(p)
  }

  renderKpis(data) {
    this.renderRangeNote(data)
    const cur = data.current
    const prev = data.previous
    const pct = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null)
    const createdDelta = pct(cur.created, prev.created)
    kpiTile(this.kpiCreatedTarget, {
      label: 'Jobs created',
      value: fmtNum(cur.created),
      delta: createdDelta,
      deltaText: createdDelta === null ? null : `${Math.abs(createdDelta)}%`,
      spark: data.spark.created
    })
    const completedDelta = pct(cur.completed, prev.completed)
    kpiTile(this.kpiCompletedTarget, {
      label: 'Jobs completed',
      value: fmtNum(cur.completed),
      delta: completedDelta,
      deltaText: completedDelta === null ? null : `${Math.abs(completedDelta)}%`,
      spark: data.spark.completed
    })
    const leadDelta = (cur.median_lead_days !== null && prev.median_lead_days !== null)
      ? Math.round((cur.median_lead_days - prev.median_lead_days) * 10) / 10 : null
    kpiTile(this.kpiLeadTarget, {
      label: 'Median lead time',
      value: cur.median_lead_days === null ? '—' : `${fmtNum(cur.median_lead_days, 1)} d`,
      delta: leadDelta,
      deltaText: leadDelta === null ? null : `${Math.abs(leadDelta)} d`,
      upIsGood: false,
      sub: 'Submitted → complete'
    })
    const otDelta = (cur.on_time_pct !== null && prev.on_time_pct !== null)
      ? cur.on_time_pct - prev.on_time_pct : null
    kpiTile(this.kpiOnTimeTarget, {
      label: 'On-time completion',
      value: cur.on_time_pct === null ? '—' : `${cur.on_time_pct}%`,
      delta: otDelta,
      deltaText: otDelta === null ? null : `${Math.abs(otDelta)} pts`,
      sub: 'vs requested completion'
    })
  }

  renderWeekly(data) {
    const groupBy = this.groupByTarget.value
    const label = this.groupByTarget.selectedOptions[0].textContent
    this.weeklySubTarget.textContent = groupBy === 'none'
      ? 'All jobs in range · click a bar to open these jobs'
      : `Stacked by ${label.toLowerCase()} · click a bar to open these jobs`
    stackedColumns(this.weeklyChartTarget, data, {
      clickHint: 'Click to view these jobs',
      onClick: () => this.drillThrough()
    })
    this.renderLegend(data.series)
    renderTable(
      this.weeklyTableTarget,
      [{ label: 'Week' }].concat(data.series.map(s => ({ label: s.label, num: true }))),
      data.weeks.map((w, i) => [`Week of ${fmtDate(w)}`].concat(data.series.map(s => fmtNum(s.values[i] || 0)))),
    )
  }

  renderLegend(series) {
    const host = this.weeklyLegendTarget
    host.innerHTML = ''
    if (!series || series.length < 2) return
    series.forEach((s, i) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = seriesColor(s.label, i)
      key.appendChild(sw)
      key.appendChild(document.createTextNode(s.label))
      host.appendChild(key)
    })
  }

  renderStatusTime(data) {
    hBars(this.statusChartTarget, data.rows.map(r => ({
      label: r.status,
      value: r.median_days,
      tipExtra: `${fmtNum(r.jobs)} jobs`
    })), { fmt: v => `${fmtNum(v, 1)} d`, unitLabel: 'median days' })
    renderTable(
      this.statusTableTarget,
      [{ label: 'Status' }, { label: 'Median days', num: true }, { label: 'Jobs', num: true }],
      data.rows.map(r => [r.status, fmtNum(r.median_days, 1), fmtNum(r.jobs)]),
    )
  }

  renderLeadTrend(data) {
    lineChart(this.leadChartTarget, data.rows.map(r => ({
      x: r.week, y: r.median_lead_days, n: r.completed
    })), { fmt: v => `${fmtNum(v, 1)} d`, seriesLabel: 'median days', nLabel: 'jobs completed' })
    renderTable(
      this.leadTableTarget,
      [{ label: 'Week' }, { label: 'Median lead (d)', num: true }, { label: 'Jobs completed', num: true }],
      data.rows.map(r => [`Week of ${fmtDate(r.week)}`, fmtNum(r.median_lead_days, 1), fmtNum(r.completed)]),
    )
  }

  // ---- Status to status time: days from a job's first entry into the
  // From status to its first entry into To after that ----

  spanNames() {
    const name = sel => (sel.selectedOptions[0] ? sel.selectedOptions[0].textContent.trim() : '')
    return { from: name(this.spanFromTarget), to: name(this.spanToTarget) }
  }

  // Own sequence (not loadSeq): changing From/To must not drop the other
  // cards' in-flight responses. Compare mode hides this card.
  loadSpan() {
    if (this.compareActive() || !this.spanFromTarget.value || !this.spanToTarget.value) return
    this.spanSeq = (this.spanSeq || 0) + 1
    const seq = this.spanSeq
    const p = toSearchParams({ ...this.filterParams(), from: this.spanFromTarget.value, to: this.spanToTarget.value })
    this.appendChipParams(p)
    const req = fetch(`${this.data.get('statusSpanUrl')}?${p}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
    trackBusy(this.busyEl(), req)
    req.then((data) => { if (seq === this.spanSeq) this.renderSpan(data) }).catch(() => {})
  }

  renderSpan(data) {
    const { from, to } = this.spanNames()
    const monthly = data.bucket === 'month'
    const unit = monthly ? 'month' : 'week'
    const days = v => (v === null || v === undefined ? '—' : `${fmtNum(v, 1)} d`)
    const pct = v => (v === null || v === undefined ? '—' : `${v}%`)
    const same = this.spanFromTarget.value === this.spanToTarget.value

    const kpis = this.spanKpisTarget
    kpis.innerHTML = ''
    const stat = (value, label, sub) => {
      const cell = document.createElement('div')
      cell.className = 'm-span-kpi'
      const v = document.createElement('div')
      v.className = 'm-span-kpi-v'
      v.textContent = value
      const l = document.createElement('div')
      l.className = 'm-span-kpi-l'
      l.textContent = label
      cell.appendChild(v)
      cell.appendChild(l)
      if (sub) {
        const t = document.createElement('div')
        t.className = 'm-span-kpi-t'
        t.textContent = sub
        cell.appendChild(t)
      }
      kpis.appendChild(cell)
    }
    stat(days(data.median_days), 'Median time', same ? 'Pick two different statuses' : `${from} to ${to}`)
    stat(fmtNum(data.jobs), `Jobs that reached ${to}`, 'in the date range')
    stat(pct(data.within_3_pct), 'Within 3 days')
    stat(pct(data.over_30_pct), '30 days or more')
    stat(fmtNum(data.waiting), 'Still waiting', `entered ${from} in range, still before ${to}`)

    this.spanLineTitleTarget.textContent = `${from} to ${to}, median by ${unit}`
    this.spanLineSubTarget.textContent = `${monthly ? 'Month' : 'Week'} the job reached ${to} · days`
    this.spanHistSubTarget.textContent = `Jobs by days from ${from} to ${to}`

    const trend = data.trend || []
    this.spanPoints = trend.map(r => ({ x: r.bucket, y: r.median_days, n: r.jobs }))
    this.spanMonthly = monthly
    lineChart(this.spanChartTarget, this.spanPoints, {
      fmt: v => `${fmtNum(v, 1)} d`,
      seriesLabel: 'median days',
      nLabel: 'jobs',
      plotH: 290,
      tipTitle: p => this.spanBucketLabel(p.x)
    })

    const bins = data.bins || []
    hBars(this.spanHistTarget, bins.map(b => ({
      label: b.to_days === null ? `${b.from_days}+ days` : `${b.from_days}-${b.to_days} days`,
      value: b.jobs,
      color: b.to_days === null ? 'var(--m-s3)' : null
    })), { unitLabel: 'jobs', labelW: 84 })

    const host = this.spanTableTarget
    host.innerHTML = ''
    const trendHost = document.createElement('div')
    const binHost = document.createElement('div')
    host.appendChild(trendHost)
    host.appendChild(binHost)
    renderTable(
      trendHost,
      [{ label: monthly ? 'Month' : 'Week' }, { label: 'Median days', num: true }, { label: 'Jobs', num: true }],
      trend.map(r => [this.spanBucketLabel(r.bucket), fmtNum(r.median_days, 1), fmtNum(r.jobs)]),
    )
    renderTable(
      binHost,
      [{ label: 'Days waited' }, { label: 'Jobs', num: true }],
      bins.map(b => [b.to_days === null ? `${b.from_days}+` : `${b.from_days} to ${b.to_days}`, fmtNum(b.jobs)]),
    )
  }

  spanBucketLabel(iso) {
    if (!this.spanMonthly) return `Week of ${fmtDate(iso)}`
    const d = new Date(`${iso}T12:00:00`)
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  // ---- "Jobs in these metrics": every job behind the cards (created or
  // completed in the range), with a checkbox to leave one out. The list
  // ignores exclude[] server-side, so excluded jobs stay listed (dimmed)
  // and can be put back; checking/unchecking reloads the cards, not the
  // list (its rows only change their flag). Hidden in compare mode. ----

  // The list's query without exclude[] (flags are kept locally): load()
  // reloads the list only when this changes.
  jobListKey() {
    const params = this.filterParams()
    delete params.exclude
    const p = toSearchParams({ ...params, ...this.jobListSort })
    this.appendChipParams(p)
    return p.toString()
  }

  loadJobList() {
    const key = this.jobListKey()
    if (key === this.jobListLoadedKey) return
    this.jobListLoadedKey = key
    this.jobListRows = []
    this.jobListTotal = 0
    this.fetchJobListPage(0)
  }

  // Own sequence (like loadSpan): an include/exclude click reloads the
  // cards (loadSeq) and must not drop a list page in flight.
  fetchJobListPage(offset) {
    this.jobListSeq = (this.jobListSeq || 0) + 1
    const seq = this.jobListSeq
    this.jobListMoreTarget.disabled = true
    const p = toSearchParams({ ...this.filterParams(), ...this.jobListSort, offset, per_page: JOB_LIST_PAGE })
    this.appendChipParams(p)
    const req = fetch(`${this.data.get('listUrl')}?${p}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
    trackBusy(this.busyEl(), req)
    req.then((data) => {
      if (seq !== this.jobListSeq) return
      // append only the page being waited on (a double click or a cached
      // duplicate would otherwise repeat rows)
      if (data.offset !== this.jobListRows.length) return
      this.jobListRows.push(...data.rows)
      this.jobListTotal = data.total
      this.jobListExcludedTotal = data.excluded_total
      this.renderJobList()
    }).catch(() => { this.jobListMoreTarget.disabled = false })
  }

  jobListSeeMore() {
    this.fetchJobListPage(this.jobListRows.length)
  }

  sortJobList(key) {
    const cur = this.jobListSort
    if (cur.sort === key) {
      this.jobListSort = { sort: key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
    } else {
      this.jobListSort = { sort: key, dir: JOB_LIST_SORT_DESC_FIRST.includes(key) ? 'desc' : 'asc' }
    }
    this.loadJobList()
  }

  setJobIncluded(row, included) {
    const id = String(row.id)
    const was = this.excluded.includes(id)
    if (included && was) this.excluded = this.excluded.filter(v => v !== id)
    if (!included && !was) this.excluded.push(id)
    if (row.excluded === !included) return
    Object.assign(row, { excluded: !included })
    this.jobListExcludedTotal = Math.max(0, (this.jobListExcludedTotal || 0) + (included ? -1 : 1))
    this.afterExclusionChange()
  }

  includeAllJobs() {
    if (!this.excluded.length) return
    this.excluded = []
    const rows = this.jobListRows || []
    rows.forEach((r) => { Object.assign(r, { excluded: false }) })
    this.jobListExcludedTotal = 0
    this.afterExclusionChange()
    this.renderJobList()
  }

  afterExclusionChange() {
    this.renderChips()
    this.renderJobListHead()
    clearTimeout(this.excludeTimer)
    this.excludeTimer = setTimeout(() => this.loadCards(), EXCLUDE_RELOAD_DELAY)
  }

  renderJobListHead() {
    const total = this.jobListTotal || 0
    const excluded = this.jobListExcludedTotal || 0
    this.jobListCountTarget.textContent = `${fmtNum(total)} ${total === 1 ? 'job' : 'jobs'}${excluded ? ` · ${fmtNum(excluded)} excluded` : ''}`
    this.jobListIncludeAllTarget.hidden = this.excluded.length === 0
  }

  renderJobList() {
    this.renderJobListHead()
    const host = this.jobListTarget
    host.innerHTML = ''
    const rows = this.jobListRows || []
    const cols = [
      { label: '', cls: 'm-jl-check' },
      { label: 'Job', sort: 'id' },
      { label: 'Name', sort: 'name' },
      { label: 'Status', sort: 'status' },
      { label: 'Assignee' },
      { label: 'Template' },
      { label: 'Created', sort: 'created' },
      { label: 'Completed', sort: 'completed' },
      { label: 'Lead time', sort: 'lead', num: true }
    ]
    const table = document.createElement('table')
    table.className = 'm-table m-jl-table'
    const trh = document.createElement('tr')
    cols.forEach((c) => {
      const th = document.createElement('th')
      if (c.cls) th.className = c.cls
      if (c.num) th.classList.add('num')
      if (c.sort) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'm-jl-sort'
        const active = this.jobListSort.sort === c.sort
        btn.textContent = c.label
        if (active) {
          btn.classList.add('active')
          btn.appendChild(document.createTextNode(this.jobListSort.dir === 'asc' ? ' ▲' : ' ▼'))
        }
        btn.title = `Sort by ${c.label.toLowerCase()}`
        btn.addEventListener('click', () => this.sortJobList(c.sort))
        th.appendChild(btn)
      } else {
        th.textContent = c.label
      }
      trh.appendChild(th)
    })
    const thead = document.createElement('thead')
    thead.appendChild(trh)
    table.appendChild(thead)

    const tbody = document.createElement('tbody')
    if (!rows.length) {
      const tr = document.createElement('tr')
      const td = document.createElement('td')
      td.colSpan = cols.length
      td.className = 'm-jl-empty'
      td.textContent = 'No jobs matching these filters were created or completed in this range.'
      tr.appendChild(td)
      tbody.appendChild(tr)
    }
    rows.forEach((row) => {
      const tr = document.createElement('tr')
      if (row.excluded) tr.className = 'm-jl-off'
      const cell = (content, cls, title) => {
        const td = document.createElement('td')
        if (cls) td.className = cls
        if (title) td.title = title
        if (content instanceof Node) td.appendChild(content)
        else td.textContent = content
        tr.appendChild(td)
        return td
      }
      const box = document.createElement('input')
      box.type = 'checkbox'
      box.checked = !row.excluded
      box.title = 'Include this job in the metrics'
      box.addEventListener('change', () => {
        tr.classList.toggle('m-jl-off', !box.checked)
        this.setJobIncluded(row, box.checked)
      })
      cell(box, 'm-jl-check')
      const link = document.createElement('a')
      link.href = `/jobs/${row.id}`
      link.className = 'm-jl-link'
      link.textContent = row.display_id
      cell(link)
      cell(row.name || '', 'm-jl-name', row.name || '')
      const status = document.createElement('span')
      status.className = 'm-jl-status'
      if (row.status_color) {
        const dot = document.createElement('span')
        dot.className = 'm-jl-dot'
        dot.style.background = row.status_color
        status.appendChild(dot)
      }
      status.appendChild(document.createTextNode(row.status || '—'))
      cell(status)
      cell(row.assignees || '—', 'm-jl-clip', row.assignees || '')
      cell(row.template || '—', 'm-jl-clip', row.template || '')
      cell(fmtDay(row.created_at), row.in_created ? '' : 'm-jl-out', row.in_created ? '' : 'Created before the range')
      cell(fmtDay(row.completed_at), row.completed_at && !row.in_completed ? 'm-jl-out' : '',
        row.completed_at && !row.in_completed ? 'Completed outside the range' : '')
      cell(row.lead_days === null || row.lead_days === undefined ? '—' : `${fmtNum(row.lead_days, 1)} d`, 'num')
      tbody.appendChild(tr)
    })
    table.appendChild(tbody)
    host.appendChild(table)

    const remaining = (this.jobListTotal || 0) - rows.length
    const more = this.jobListMoreTarget
    more.disabled = false
    more.hidden = remaining <= 0
    more.textContent = `See more (${fmtNum(remaining)} remaining)`
  }

  // ---- comparison groups: N sets of job-column filters layered on the
  // shared filters; the same endpoints are fetched once per group ----

  compareActive() {
    return this.groups.length >= 2
  }

  startCompare() {
    this.groups = [{ slot: 0, filters: [] }, { slot: 1, filters: [] }]
    this.enterCompare()
    this.load()
  }

  enterCompare() {
    renderEmptyNote(this.emptyNoteTarget, null)
    this.renderGroupRows()
    this.singleGridTarget.style.display = 'none'
    this.compareGridTarget.style.display = 'grid'
    this.compareBtnTarget.style.display = 'none'
    // Group by only applies to the single-mode chart — in compare mode the
    // main chart is grouped by comparison group (matches the mock).
    this.groupByTarget.closest('.m-filter').style.display = 'none'
  }

  exitCompare() {
    this.groups = []
    this.groupRowsTarget.innerHTML = ''
    this.singleGridTarget.style.display = 'grid'
    this.compareGridTarget.style.display = 'none'
    this.compareBtnTarget.style.display = ''
    this.groupByTarget.closest('.m-filter').style.display = ''
    this.load()
  }

  groupSummary(group) {
    return group.filters.map(f => `${f.columnName}: ${f.value}`).join(' · ') || 'All matching jobs'
  }

  renderGroupRows() {
    const host = this.groupRowsTarget
    const options = JSON.parse(host.dataset.columnOptions || '[]')
    host.innerHTML = ''
    this.groups.forEach((group, gi) => {
      const row = document.createElement('div')
      row.className = 'm-grp-row'
      const dot = document.createElement('span')
      dot.className = 'm-gdot'
      dot.style.background = SLOTS[group.slot]
      row.appendChild(dot)
      const name = document.createElement('span')
      name.className = 'm-gname'
      name.textContent = GROUP_NAMES[group.slot]
      row.appendChild(name)

      group.filters.forEach((f, fi) => {
        const chip = document.createElement('span')
        chip.className = 'm-chip'
        const label = document.createElement('span')
        label.textContent = `${f.columnName}: `
        const strong = document.createElement('strong')
        strong.textContent = f.value
        label.appendChild(strong)
        chip.appendChild(label)
        const x = document.createElement('button')
        x.type = 'button'
        x.textContent = '✕'
        x.title = 'Remove filter'
        x.addEventListener('click', () => {
          group.filters.splice(fi, 1)
          this.renderGroupRows()
          this.load()
        })
        chip.appendChild(x)
        row.appendChild(chip)
      })

      const colSel = document.createElement('select')
      colSel.appendChild(new Option('+ Add job column…', ''))
      options.forEach(o => colSel.appendChild(new Option(o.name, o.id)))
      const valueSel = document.createElement('select')
      valueSel.appendChild(new Option('value…', ''))
      valueSel.disabled = true
      colSel.addEventListener('change', () => {
        valueSel.innerHTML = ''
        valueSel.appendChild(new Option('Loading…', ''))
        valueSel.disabled = true
        if (!colSel.value) return
        this.fetchJson(`${this.data.get('columnValuesUrl')}?column_id=${colSel.value}`, (data) => {
          valueSel.innerHTML = ''
          valueSel.appendChild(new Option('value…', ''))
          data.values.forEach(v => valueSel.appendChild(new Option(v, v)))
          valueSel.disabled = false
        })
      })
      valueSel.addEventListener('change', () => {
        if (!colSel.value || !valueSel.value) return
        group.filters.push({
          columnId: colSel.value,
          columnName: colSel.selectedOptions[0].textContent,
          value: valueSel.value
        })
        this.renderGroupRows()
        this.load()
      })
      row.appendChild(colSel)
      row.appendChild(valueSel)

      if (this.groups.length > 2) {
        const rm = document.createElement('button')
        rm.type = 'button'
        rm.className = 'm-btn'
        rm.textContent = '✕ Remove group'
        rm.addEventListener('click', () => {
          this.groups.splice(gi, 1)
          this.groups.forEach((g, i) => { Object.assign(g, { slot: i }) })
          this.renderGroupRows()
          this.load()
        })
        row.appendChild(rm)
      }
      host.appendChild(row)
    })

    const actions = document.createElement('div')
    actions.className = 'm-grp-actions'
    if (this.groups.length < 3) {
      const add = document.createElement('button')
      add.type = 'button'
      add.className = 'm-btn'
      add.textContent = '+ Add comparison group'
      add.addEventListener('click', () => {
        this.groups.push({ slot: this.groups.length, filters: [] })
        this.renderGroupRows()
        this.load()
      })
      actions.appendChild(add)
    }
    const exit = document.createElement('button')
    exit.type = 'button'
    exit.className = 'm-btn'
    exit.textContent = 'Exit compare'
    exit.addEventListener('click', () => this.exitCompare())
    actions.appendChild(exit)
    host.appendChild(actions)
  }

  groupQueryString(group, extra = {}) {
    const p = toSearchParams({ ...this.filterParams(), ...extra })
    this.appendChipParams(p)
    group.filters.forEach(f => p.append('columns[]', `${f.columnId}:${f.value}`))
    return p.toString()
  }

  loadCompare() {
    const seq = this.loadSeq
    const fetches = this.groups.map(group => Promise.all([
      fetch(`${this.data.get('kpisUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('weeklyUrl')}?${this.groupQueryString(group, { group_by: 'status' })}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('leadTrendUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('statusTimeUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json())
    ]))
    trackBusy(this.busyEl(), Promise.all(fetches)).then((results) => {
      if (seq !== this.loadSeq) return
      this.renderCompare(results)
    }).catch(() => {})
  }

  renderCompare(results) {
    const groups = this.groups.map((group, i) => ({
      group,
      kpis: results[i][0],
      weekly: results[i][1],
      leadTrend: results[i][2].rows,
      statusTime: results[i][3].rows,
      color: SLOTS[group.slot],
      name: GROUP_NAMES[group.slot]
    }))
    const { weeks } = groups[0].weekly

    const tiles = this.cmpTilesTarget
    tiles.innerHTML = ''
    groups.forEach((g) => {
      const tile = document.createElement('div')
      tile.className = 'm-card m-cmp-tile'
      const cur = g.kpis.current
      kpiTile(tile, {
        label: `${g.name} — median lead time`,
        dot: g.color,
        value: cur.median_lead_days === null ? '—' : `${fmtNum(cur.median_lead_days, 1)} d`,
        delta: null,
        deltaText: null,
        sub: `${this.groupSummary(g.group)} · ${fmtNum(cur.created)} jobs${cur.on_time_pct === null ? '' : ` · on-time ${cur.on_time_pct}%`}`.slice(0, 90)
      })
      tiles.appendChild(tile)
    })

    const legend = this.cmpLegendTarget
    legend.innerHTML = ''
    groups.forEach((g) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = g.color
      key.appendChild(sw)
      key.appendChild(document.createTextNode(`${g.name}: ${this.groupSummary(g.group)}`.slice(0, 60)))
      legend.appendChild(key)
    })

    groupedColumns(this.cmpWeeklyTarget, {
      weeks,
      groups: groups.map(g => ({
        label: g.name,
        color: g.color,
        values: weeks.map((_, i) => g.weekly.series.reduce((a, s) => a + (s.values[i] || 0), 0))
      }))
    })

    multiLine(this.cmpLeadTarget, {
      weeks,
      series: groups.map(g => ({
        label: g.name,
        color: g.color,
        points: weeks.map((w) => {
          const row = g.leadTrend.find(r => r.week === w)
          return row ? row.median_lead_days : null
        })
      }))
    }, { percent: false, fmt: v => `${fmtNum(v, 1)} d` })

    const statusLabels = []
    groups.forEach(g => g.weekly.series.forEach((s) => {
      if (!statusLabels.includes(s.label)) statusLabels.push(s.label)
    }))
    const mixLegend = this.cmpMixLegendTarget
    mixLegend.innerHTML = ''
    statusLabels.forEach((label, i) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = seriesColor(label, i)
      key.appendChild(sw)
      key.appendChild(document.createTextNode(label))
      mixLegend.appendChild(key)
    })
    hBarsStacked(this.cmpMixTarget, groups.map(g => ({
      label: g.name,
      total: g.weekly.series.reduce((a, s) => a + s.values.reduce((x, y) => x + y, 0), 0),
      parts: statusLabels.map((label, i) => ({
        label,
        color: seriesColor(label, i),
        value: (g.weekly.series.find(s => s.label === label) || { values: [] }).values.reduce((x, y) => x + y, 0)
      }))
    })))

    const statuses = []
    groups.forEach(g => g.statusTime.forEach((r) => {
      if (!statuses.includes(r.status)) statuses.push(r.status)
    }))
    renderTable(
      this.cmpStatusTableTarget,
      [{ label: 'Status' }].concat(groups.map(g => ({ label: g.name, num: true }))),
      statuses.map(status => [status].concat(groups.map((g) => {
        const row = g.statusTime.find(r => r.status === status)
        return row ? `${fmtNum(row.median_days, 1)} d` : '—'
      }))),
    )
  }

  switchTab(ev) {
    this.activateTab(ev.currentTarget.dataset.tab)
  }

  activateTab(tab) {
    this.element.querySelectorAll('.m-tab-btn[data-tab]').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tab === tab)
    })
    this.jobsPanelTarget.classList.toggle('active', tab === 'jobs')
    this.resultsPanelTarget.classList.toggle('active', tab === 'results')
    this.trendsPanelTarget.classList.toggle('active', tab === 'trends')
    // Only present when the account has the Technician Dashboard configured
    if (this.hasTechnicianPanelTarget) this.technicianPanelTarget.classList.toggle('active', tab === 'technician')
    this.filterViewPills(tab)
    // re-render the newly visible tab: its charts may hold widths measured
    // when the window was a different size (or the panel hidden)
    if (tab === 'jobs') this.loadCards()
    if (tab === 'results') window.dispatchEvent(new CustomEvent('metrics:results-shown'))
    if (tab === 'trends') window.dispatchEvent(new CustomEvent('metrics:trends-shown'))
    if (tab === 'technician') window.dispatchEvent(new CustomEvent('metrics:technician-shown'))
  }

  // Saved views are tab-scoped: only the active tab's views show as pills
  // or drawer rows (they all share page_name metrics#show for routability).
  filterViewPills(tab) {
    this.element.querySelectorAll('[data-view-tab]').forEach((pill) => {
      const el = pill
      el.style.display = el.dataset.viewTab === tab ? '' : 'none'
    })
    const visibleDrawerRows = [...this.element.querySelectorAll('.m-more-row')]
      .filter(el => el.dataset.viewTab === tab).length
    const more = this.element.querySelector('.m-more')
    if (more) {
      more.style.display = visibleDrawerRows ? '' : 'none'
      if (this.hasMoreCountTarget) this.moreCountTarget.textContent = visibleDrawerRows
    }
  }

  toggleTable(ev) {
    const btn = ev.currentTarget
    const card = btn.closest('.m-card')
    const showing = card.classList.toggle('show-table')
    btn.textContent = showing ? 'Chart' : 'Table'
  }

  // The jobs index takes arrays for assignee[], requester[] and
  // parent_template_id[] too (JobIndexFilters matches them with IN).
  drillThrough() {
    const params = toSearchParams(this.filterParams())
    params.delete('range') // jobs index has no created_at filter
    // the jobs index has no way to leave single jobs out: exclusions don't carry
    params.delete('exclude[]')
    // exact tag and job column picks carry over; "All containing" terms and
    // job fields can't (the jobs index has no contains or job-field filter)
    this.jobChips.drillParams(params)
    window.location.href = `${this.data.get('jobsUrl')}?${params.toString()}`
  }

  // ---- saved views (saved_search_params, page_name metrics#show) ----
  // Views open via their URL (params + saved_search_id); the page applies
  // URL params on load. Star = landing default, thumbtack = pinned pill.

  setSelect(selectEl, value, fallback) {
    const el = selectEl
    el.value = value || fallback
    // a saved value may name a status or option that is gone; fall back cleanly
    if (el.value !== String(value || fallback)) el.value = fallback
  }

  activeTab() {
    if (this.resultsPanelTarget.classList.contains('active')) return 'results'
    if (this.trendsPanelTarget.classList.contains('active')) return 'trends'
    if (this.hasTechnicianPanelTarget && this.technicianPanelTarget.classList.contains('active')) return 'technician'
    return 'jobs'
  }

  currentViewParams() {
    const tab = this.activeTab()
    if (tab !== 'jobs') {
      const identifier = `metrics-${tab}`
      const panels = {
        results: this.resultsPanelTarget,
        trends: this.trendsPanelTarget,
        technician: this.hasTechnicianPanelTarget ? this.technicianPanelTarget : null
      }
      const controller = panels[tab]
        && this.application.getControllerForElementAndIdentifier(panels[tab], identifier)
      if (controller) return controller.viewParams()
    }
    const params = {
      tab: 'jobs',
      ...this.filterParams(),
      group_by: this.groupByTarget.value,
      span_from: this.spanFromTarget.value,
      span_to: this.spanToTarget.value
    }
    this.jobChips.viewParams(params)
    if (this.compareActive()) {
      params.groups = this.groups.map(g => g.filters.map(f => `${f.columnId}:${f.value}`).join('|'))
    }
    return params
  }

  saveView() {
    const name = window.prompt('Name this view:')
    if (!name) return
    fetch(this.data.get('savedSearchUrl'), {
      method: 'POST',
      headers: {
        'X-CSRF-Token': Rails.csrfToken(),
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        saved_search_param: { name, page_name: 'metrics#show', params: JSON.stringify(this.currentViewParams()) }
      })
    }).then((r) => { if (r.ok) window.location.reload() })
  }

  togglePin(ev) {
    this.patchView(ev.currentTarget.dataset.viewId, 'toggle_pinned')
  }

  toggleStar(ev) {
    this.patchView(ev.currentTarget.dataset.viewId, 'set_default')
  }

  // Star on the "Overview" system pill: clears the current landing view.
  clearDefault(ev) {
    const id = ev.currentTarget.dataset.defaultId
    if (id) this.patchView(id, 'set_default')
  }

  patchView(id, action) {
    fetch(`${this.data.get('savedSearchUrl')}/${id}/${action}`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' }
    }).then(() => window.location.reload())
  }

  toggleMoreViews() {
    this.moreMenuTarget.classList.toggle('open')
  }

  // ---- per-pill kebab menu (one shared menu, jobs-index pattern) ----

  openViewMenu(ev) {
    ev.stopPropagation()
    const pill = ev.currentTarget.closest('.m-vpill')
    this.menuViewId = pill.dataset.viewId
    this.menuViewName = pill.dataset.viewName
    this.menuViewParams = pill.dataset.viewParams
    this.menuDefaultLabelTarget.textContent = pill.dataset.viewDefault === 'true'
      ? 'Remove as landing view' : 'Make this my landing view'
    this.menuPinLabelTarget.textContent = pill.dataset.viewPinned === 'true'
      ? 'Unpin from bar' : 'Pin to bar'
    const rect = ev.currentTarget.getBoundingClientRect()
    const menu = this.viewMenuTarget
    menu.style.top = `${rect.bottom + 4}px`
    menu.style.left = `${Math.max(8, rect.right - 220)}px`
    menu.classList.add('open')
    this.closeMenuHandler = () => this.closeViewMenu()
    setTimeout(() => document.addEventListener('click', this.closeMenuHandler, { once: true }), 0)
  }

  closeViewMenu() {
    this.viewMenuTarget.classList.remove('open')
  }

  menuToggleDefault(ev) {
    ev.stopPropagation()
    this.patchView(this.menuViewId, 'set_default')
  }

  menuTogglePin(ev) {
    ev.stopPropagation()
    this.patchView(this.menuViewId, 'toggle_pinned')
  }

  menuMoveLeft(ev) {
    ev.stopPropagation()
    this.moveView('left')
  }

  menuMoveRight(ev) {
    ev.stopPropagation()
    this.moveView('right')
  }

  // The shared move endpoint swaps one slot in the FULL page ordering, but
  // metrics pills are filtered by tab (hidden neighbors sit between visible
  // ones) — so step across as many slots as it takes to pass the adjacent
  // VISIBLE pill, then reload.
  moveView(direction) {
    this.closeViewMenu()
    const fullOrder = JSON.parse(this.element.querySelector('.m-views').dataset.viewOrder || '[]').map(String)
    const visibleIds = [...this.element.querySelectorAll('.m-vpill[data-view-id]')]
      .filter(pill => pill.style.display !== 'none')
      .map(pill => pill.dataset.viewId)
    const vIdx = visibleIds.indexOf(this.menuViewId)
    const neighbor = direction === 'left' ? visibleIds[vIdx - 1] : visibleIds[vIdx + 1]
    if (!neighbor) return
    const steps = Math.abs(fullOrder.indexOf(this.menuViewId) - fullOrder.indexOf(neighbor))
    if (steps < 1) return
    let chain = Promise.resolve()
    for (let i = 0; i < steps; i += 1) {
      chain = chain.then(() => fetch(`${this.data.get('savedSearchUrl')}/${this.menuViewId}/move?direction=${direction}`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' }
      }))
    }
    chain.then(() => window.location.reload())
  }

  menuRename(ev) {
    ev.stopPropagation()
    this.closeViewMenu()
    const name = window.prompt('Rename view:', this.menuViewName)
    if (!name || name === this.menuViewName) return
    fetch(`${this.data.get('savedSearchUrl')}/${this.menuViewId}`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ saved_search_param: { name } })
    }).then((r) => { if (r.ok) window.location.reload() })
  }

  menuDuplicate(ev) {
    ev.stopPropagation()
    this.closeViewMenu()
    fetch(this.data.get('savedSearchUrl'), {
      method: 'POST',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        saved_search_param: {
          name: `${this.menuViewName} (copy)`,
          page_name: 'metrics#show',
          params: this.menuViewParams
        }
      })
    }).then((r) => { if (r.ok) window.location.reload() })
  }

  menuDelete(ev) {
    ev.stopPropagation()
    this.closeViewMenu()
    if (!window.confirm('Delete this saved view?')) return
    fetch(`${this.data.get('savedSearchUrl')}/${this.menuViewId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' }
    }).then(() => window.location.reload())
  }
}
